import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Download,
  Play,
  RefreshCw,
  IndianRupee,
  Users,
  Calculator,
  X,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Button from '../../../components/common/Button/Button';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { toast } from 'react-hot-toast';
import { payrollRunApi } from '../../../api/payrollRunApi';
import { formatDate } from '../../../utils/stringUtils';
import WarningPopup from '../../../components/WarningPopup';
import ExportModal from '../../../components/common/ExportModal';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import useUrlTableState from '../../../hooks/useUrlTableState';

const formatDateValue = (value, options = { includeTime: false }) => {
  if (!value) return 'N/A';
  const formatted = formatDate(value, options);
  return formatted || 'N/A';
};

const formatPeriodDisplay = (run) => {
  if (!run) return 'N/A';

  const start = run.payPeriodStart;
  const end = run.payPeriodEnd;

  if (start || end) {
    const formattedStart = start ? formatDateValue(start, { includeTime: false }) : '';
    const formattedEnd = end ? formatDateValue(end, { includeTime: false }) : '';

    if (formattedStart && formattedEnd) return `${formattedStart} - ${formattedEnd}`;
    if (formattedStart) return formattedStart;
    if (formattedEnd) return formattedEnd;
  }

  const period = run.period;
  if (!period) return 'N/A';

  const monthYearMatch = /^(\d{4})-(\d{2})$/;
  if (monthYearMatch.test(period)) {
    const [, year, month] = period.match(monthYearMatch);
    const parsedDate = new Date(Number(year), Number(month) - 1, 1);
    const formatted = formatDate(parsedDate, { includeTime: false });
    return formatted || period;
  }
  const formatted = formatDate(period, { includeTime: false });
  return formatted || period;
};

const formatPayGroupName = (name) => {
  if (!name) return 'N/A';
  const cleaned = name.replace(/\s*\(Default\)$/i, '').trim();
  return cleaned || name;
};

const formatCurrencyInr = (value) => {
  const amount = Number(value) || 0;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
};

const PayrollRun = () => {
  const navigate = useNavigate();

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    statusFilter: 'all',
    runStartDate: '',
    runEndDate: '',
    minNetPay: '',
    maxNetPay: ''
  }), []);

  const {
    filters: payrollFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updatePayrollFilter,
    updatePage: updatePayrollPage,
    updateLimit: updatePayrollLimit,
    resetFilters: resetPayrollFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const page = payrollFilters.page;
  const limit = payrollFilters.limit;

  const statusFilter = payrollFilters.statusFilter || 'all';
  const setStatusFilter = useCallback((statusFilter) => {
    updatePayrollFilter({ statusFilter });
  }, [updatePayrollFilter]);

  const runStartDate = payrollFilters.runStartDate || '';
  const runEndDate = payrollFilters.runEndDate || '';

  const setDateRange = useCallback((range) => {
    updatePayrollFilter({ runStartDate: range.startDate || '', runEndDate: range.endDate || '' });
  }, [updatePayrollFilter]);

  const setRunStartDate = useCallback((startDate) => {
    updatePayrollFilter({ runStartDate: startDate });
  }, [updatePayrollFilter]);

  const setRunEndDate = useCallback((endDate) => {
    updatePayrollFilter({ runEndDate: endDate });
  }, [updatePayrollFilter]);

  const minNetPay = payrollFilters.minNetPay || '';
  const setMinNetPay = useCallback((minNetPay) => {
    updatePayrollFilter({ minNetPay });
  }, [updatePayrollFilter]);

  const maxNetPay = payrollFilters.maxNetPay || '';
  const setMaxNetPay = useCallback((maxNetPay) => {
    updatePayrollFilter({ maxNetPay });
  }, [updatePayrollFilter]);

  const setPage = useCallback((page) => {
    updatePayrollPage(page);
  }, [updatePayrollPage]);

  const setLimit = useCallback((limit) => {
    updatePayrollLimit(limit);
  }, [updatePayrollLimit]);

  const [showRunModal, setShowRunModal] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);

  const [runFormData, setRunFormData] = useState({
    payGroup: '',
    period: '',
    // runType: 'regular',
    includeAdjustments: false,
    includeReimbursements: false,
    includeLoans: false
  });

  const queryClient = useQueryClient();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isBulkDelete, setIsBulkDelete] = useState(false);
  const [runToDelete, setRunToDelete] = useState(null);
  const [isSubmittingRun, setIsSubmittingRun] = useState(false);

  const payrollRunsQueryKey = [
    'payroll',
    'runs',
    searchTerm,
    statusFilter,
    runStartDate,
    runEndDate,
    minNetPay,
    maxNetPay,
    page,
    limit
  ];

  const {
    data: responseData,
    isLoading: payrollRunsLoading,
    isFetching: payrollRunsFetching
  } = useQuery({
    queryKey: payrollRunsQueryKey,
    queryFn: async () => {
      const response = await payrollRunApi.getPayrollRuns({
        search: searchTerm,
        status: statusFilter,
        runStartDate,
        runEndDate,
        minNetPay,
        maxNetPay,
        page,
        limit
      });
      if (response?.responseCode === 200) {
        return response;
      }
      toast.error(response?.responseMessage || 'Failed to load payroll runs');
      return { data: [], pagination: null };
    },
    keepPreviousData: true,
    enabled: !minNetPay || !maxNetPay || Number(maxNetPay) >= Number(minNetPay)
  });

  const payrollRuns = responseData?.data || [];
  const pagination = responseData?.pagination || null;

  const createPayrollRunMutation = useMutation({
    mutationFn: async (payload) => {
      try {
        const response = await payrollRunApi.createPayrollRun(payload);
        return response;
      } catch (error) {
        // Preserve the error with all its properties
        throw error;
      }
    },
    onMutate: () => {
      setIsSubmittingRun(true);
    },
    onSuccess: (response) => {
      // onSuccess is only called when the request succeeds, so always show success
      // Extract meaningful success message from response data
      const successMessage = response?.responseMessage ||
        response?.data?.message ||
        (response?.data?.payrollRunId
          ? `Payroll run created successfully for ${response.data.payPeriod || 'the selected period'}`
          : 'Payroll run created successfully');

      toast.success(successMessage);
      setShowRunModal(false);

      queryClient.invalidateQueries(['payroll', 'runs']);
    },
    onError: (error) => {
      // Extract error message from backend response
      // The apiClient interceptor throws Error with responseMessage for 409 errors
      let errorMessage = 'Error creating payroll run';

      // Priority 1: Check error.message (apiClient throws Error with message)
      if (error?.message && error.message !== 'Error creating payroll run') {
        errorMessage = error.message;
      }
      // Priority 2: Check response.data.responseMessage
      else if (error?.response?.data?.responseMessage) {
        errorMessage = error.response.data.responseMessage;
      }
      // Priority 3: Check response.data.message
      else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      // Priority 4: Check response.data (if it's a string)
      else if (typeof error?.response?.data === 'string') {
        errorMessage = error.response.data;
      }

      // Show error toast with the backend message
      // Ensure we have a valid message before showing toast
      if (errorMessage && errorMessage.trim()) {
        toast.error(errorMessage, {
          duration: 4000,
          position: 'top-right',
          style: {
            background: '#fee2e2',
            color: '#991b1b',
            padding: '16px',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '500',
          },
        });
      } else {
        toast.error('An error occurred while creating the payroll run');
      }
    },
    onSettled: () => {
      setIsSubmittingRun(false);
    }
  });

  const deletePayrollRunMutation = useMutation({
    mutationFn: (id) => payrollRunApi.deletePayrollRun(id),
    onSuccess: (response) => {
      const responseCode = response?.responseCode ?? response?.status ?? response?.statusCode;
      const message = response?.responseMessage || response?.message || 'Payroll run deleted successfully';

      const isSuccess =
        responseCode === 200 ||
        responseCode === 204 ||
        response?.success === true ||
        message.toLowerCase().includes('success');

      if (isSuccess) {
        // toast.success(message); // Removed to avoid duplicate notifications
        queryClient.invalidateQueries(['payroll', 'runs']);
      } else {
        toast.error(message || 'Failed to delete payroll run');
      }
    },
    onError: (error) => {
      toast.error(error?.response?.data?.responseMessage || 'Error deleting payroll run');
    }
  });

  const isTableLoading = payrollRunsLoading || payrollRunsFetching;
  const isCreatingRun = isSubmittingRun || createPayrollRunMutation.isLoading;

  // Export payroll runs


  // Refresh payroll runs
  const handleRefresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['payroll', 'runs'] });
  }, [queryClient]);

  const handleRunPayroll = () => {
    if (isCreatingRun || !validateRunForm()) {
      return;
    }
    createPayrollRunMutation.mutate(runFormData);
  };

  // Validate form
  const validateRunForm = () => {
    const errors = {};

    if (!runFormData.payGroup) {
      errors.payGroup = 'Pay group is required';
    }

    if (!runFormData.period?.trim()) {
      errors.period = 'Period is required';
    } else if (!/^\d{4}-\d{2}$/.test(runFormData.period)) {
      errors.period = 'Period must be in YYYY-MM format (e.g., 2024-01)';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleDeletePayrollRun = (run) => {
    setRunToDelete(run);
    setIsBulkDelete(false);
    setShowDeleteConfirm(true);
  };

  const handleBulkDelete = () => {
    if (selectedRows.size === 0) return;
    setIsBulkDelete(true);
    setShowDeleteConfirm(true);
  };

  const confirmDeletePayrollRun = async () => {
    if (!runToDelete && !isBulkDelete) return;

    try {
      if (isBulkDelete) {
        const ids = Array.from(selectedRows);
        let successCount = 0;
        let failCount = 0;

        for (const id of ids) {
          try {
            await deletePayrollRunMutation.mutateAsync(id);
            successCount++;
          } catch (error) {
            failCount++;
          }
        }

        if (successCount > 0) {
          toast.success(`${successCount} payroll run(s) deleted successfully.`);
          setSelectedRows(new Set());
        }
        if (failCount > 0) {
          toast.error(`${failCount} payroll run(s) could not be deleted.`);
        }
      } else {
        await deletePayrollRunMutation.mutateAsync(runToDelete.id || runToDelete._id);
        toast.success('Payroll run deleted successfully.');
      }
      setShowDeleteConfirm(false);
      setRunToDelete(null);
      setIsBulkDelete(false);
    } catch (error) {
      if (!isBulkDelete) {
        toast.error('Failed to delete payroll run. Please try again.');
      }
    }
  };

  const cancelDeletePayrollRun = () => {
    setShowDeleteConfirm(false);
    setRunToDelete(null);
    setIsBulkDelete(false);
  };

  const sortedRuns = payrollRuns;

  // Frontend sorting removed as we are moving to backend logic where possible
  // and user task specifically asked for removing frontend logic

  // Reset selected rows when filters or search change
  useEffect(() => {
    setSelectedRows(new Set());
  }, [
    searchTerm,
    statusFilter,
    runStartDate,
    runEndDate,
    minNetPay,
    maxNetPay
  ]);

  // Click-outside handler for inline export format bar
  useEffect(() => {
    const onMouseDown = (event) => {
      if (!showInlineSelectedFormats) return;
      const target = event.target;
      const wrap = selectedExportWrapRef.current;
      const bar = target.closest('[data-export-format-bar]');
      if (wrap && !wrap.contains(target) && !bar) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  // Build base export filters
  const baseExportFilters = useMemo(() => {
    const apiFilters = {
      search: searchTerm || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
      runStartDate: runStartDate || undefined,
      runEndDate: runEndDate || undefined,
      minNetPay: minNetPay || undefined,
      maxNetPay: maxNetPay || undefined,
    };

    Object.keys(apiFilters).forEach((key) => {
      if (apiFilters[key] === undefined) delete apiFilters[key];
    });

    return apiFilters;
  }, [searchTerm, statusFilter, runStartDate, runEndDate, minNetPay, maxNetPay]);

  // Get current page run IDs
  const currentPageRunIds = useMemo(
    () =>
      sortedRuns
        .map((r) => {
          const raw = r?._id ?? r?.id;
          return raw != null && raw !== '' ? String(raw) : '';
        })
        .filter(Boolean),
    [sortedRuns]
  );

  // Unified backend export function using /exports/generate endpoint
  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds = [],
      overrideFilters = null,
      overridePage = null,
      overrideLimit = null,
      currentPageIds: overrideCurrentPageIds = null,
    }) => {
      const token = localStorage.getItem('auth_token');
      const pageIds =
        scope === 'currentPage'
          ? (overrideCurrentPageIds ?? currentPageRunIds)
          : [];
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/exports/generate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            module: 'payroll-runs',
            format,
            scope,
            filters: overrideFilters ?? baseExportFilters,
            page: overridePage ?? page,
            limit: overrideLimit ?? limit,
            sortBy: 'createdAt',
            sortOrder: 'desc',
            selectedIds,
            currentPageIds: Array.isArray(pageIds) ? pageIds : [],
            pdfFormat: format === 'pdf' ? 'a4' : undefined,
          }),
        }
      );

      if (!response.ok) {
        const text = await response.text();
        let msg = text || 'Export request failed';
        try {
          const j = JSON.parse(text);
          if (j?.responseMessage || j?.message) {
            msg = j.responseMessage || j.message;
          }
        } catch {
          /* plain text */
        }
        throw new Error(msg);
      }

      const blob = await response.blob();
      const disposition = response.headers.get('content-disposition') || '';
      const filenameMatch =
        disposition.match(/filename\*=UTF-8''([^;]+)/i) ||
        disposition.match(/filename="?([^"]+)"?/i);
      const fallbackName = `payroll-runs-export.${format === 'xlsx' ? 'xlsx' : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, '')
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [
      baseExportFilters,
      page,
      limit,
      currentPageRunIds,
    ]
  );

  // Handle export for selected runs with format selection
  const handleSelectedFormatExport = useCallback(
    async (format) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) return;
      const ids = Array.from(selectedRows).map(String).filter(Boolean);
      if (!ids.length) {
        toast.error('No selected runs to export.');
        return;
      }
      setExportLoading(true);
      try {
        await requestBackendExport({
          scope: 'selected',
          format,
          selectedIds: ids,
        });
        toast.success('Export started.');
        setShowInlineSelectedFormats(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [selectedRows, requestBackendExport]
  );

  // Handle scoped export from modal
  const handleScopedExport = useCallback(
    async (scope, format, meta = {}) => {
      if (!['csv', 'xlsx', 'pdf'].includes(format)) {
        return;
      }
      setExportLoading(true);
      try {
        if (scope === 'dateRange') {
          const { startDate, endDate } = meta;
          if (!startDate || !endDate) {
            toast.error('Please select both start and end dates.');
            return;
          }
          await requestBackendExport({
            scope: 'dateRange',
            format,
            overrideFilters: {
              ...baseExportFilters,
              runStartDate: startDate,
              runEndDate: endDate,
            },
          });
        } else if (scope === 'all') {
          await requestBackendExport({
            scope: 'all',
            format,
            overrideFilters: {},
            overridePage: 1,
            overrideLimit: limit,
          });
        } else if (scope === 'filtered') {
          await requestBackendExport({
            scope: 'filtered',
            format,
          });
        } else if (scope === 'currentPage') {
          await requestBackendExport({
            scope: 'currentPage',
            format,
            currentPageIds: currentPageRunIds,
          });
        } else {
          return;
        }

        toast.success('Export started.');
        setShowExportModal(false);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || 'Export failed.');
      } finally {
        setExportLoading(false);
      }
    },
    [
      sortedRuns.length,
      baseExportFilters,
      requestBackendExport,
      limit,
      currentPageRunIds,
    ]
  );

  // Export click handler
  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedRows(newSelected);
  };

  const handleSelectAll = (e) => {
    const currentPageIds = sortedRuns.map(run => run.id || run._id);
    const selectedOnPage = currentPageIds.filter(id => selectedRows.has(id));

    if (selectedOnPage.length > 0) {
      // If any selected on current page, clear EVERYTHING
      setSelectedRows(new Set());
    } else {
      // Otherwise select all on current page
      const newSelected = new Set(selectedRows);
      currentPageIds.forEach(id => newSelected.add(id));
      setSelectedRows(newSelected);
    }
  };

  // Handle key press for text inputs (alphanumeric, spaces, commas only)
  const handleTextKeyPress = (e) => {
    if (!/^[a-zA-Z0-9\s,]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
    }
  };

  // Optimized form handlers to prevent cursor jumping
  const handleRunFormChange = useCallback((field, value) => {
    let processedValue = value;

    // Period field is a month input (YYYY-MM format), so preserve the value as-is
    if (field === 'period') {
      // Month input type returns YYYY-MM format, so we don't need to filter it
      processedValue = value;
    } else {
      // Text fields: only allow alphanumeric, spaces, and commas
      processedValue = value.replace(/[^a-zA-Z0-9\s,]/g, '');
      // Enforce maxLength
      if (processedValue.length > 50) processedValue = processedValue.slice(0, 50);
    }

    setRunFormData(prev => ({
      ...prev,
      [field]: processedValue || value
    }));

    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  }, [formErrors]);


  const handleCloseRunModal = useCallback(() => {
    setShowRunModal(false);
  }, []);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payroll Run</h1>
          <p className="text-gray-600 mt-1">Execute payroll runs and manage processing</p>
        </div>
        <div className="mt-4 sm:mt-0 flex items-center space-x-4">
          {selectedRows.size > 0 && (
            <PermissionGate permission="payroll" action="delete">
              <Button
                variant="primary"
                icon={<Trash2 className="h-4 w-4" />}
                onClick={handleBulkDelete}
                className="bg-red-600 hover:bg-red-700 border-red-600"
              >
                Delete ({selectedRows.size})
              </Button>
            </PermissionGate>
          )}
          <PermissionGate permission="payroll" action="read">
            <div ref={selectedExportWrapRef} className="relative">
              <Button
                variant="outline"
                icon={<Download className="h-4 w-4" />}
                onClick={handleExportClick}
              >
                {selectedRows.size > 0 ? `Export (${selectedRows.size})` : 'Export'}
              </Button>
              {showInlineSelectedFormats && (
                <SelectedExportFormatBar
                  onFormatSelect={handleSelectedFormatExport}
                  loading={exportLoading}
                  onClose={() => setShowInlineSelectedFormats(false)}
                  onMouseEnter={() => setInlineFormatPanelEntered(true)}
                  onMouseLeave={() => setInlineFormatPanelEntered(false)}
                />
              )}
            </div>
          </PermissionGate>
          <PermissionGate permission="payroll" action="create">
            <Button
              variant="primary"
              icon={<Play className="h-4 w-4" />}
              onClick={() => {
                // Open with current values; no hardcoded defaults
                setShowRunModal(true);
              }}
              className="bg-brand hover:bg-brand-dark text-white border-transparent"
            >
              Run Payroll
            </Button>
          </PermissionGate>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="relative flex-grow min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by Pay Group..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                setSearchTerm(value);
              }}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:ring-brand focus:outline-none focus:border-brand"
            />
            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="flex-shrink-0">
            <label className="block text-xs font-semibold text-gray-900 uppercase mb-1 ml-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-0 focus:ring-brand focus:outline-none focus:border-brand"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>


          <div className="flex-shrink-0">
            <label className="block text-xs font-semibold text-gray-900 uppercase mb-1 ml-1">Run Date Range</label>
            <DateRangeFilter
              startDate={runStartDate}
              endDate={runEndDate}
              onChange={setDateRange}
              inputClassName="w-32 px-2 py-2 border border-gray-300 rounded-lg text-sm focus:ring-0 focus:ring-brand focus:outline-none focus:border-brand"
            />
          </div>

          <div className="flex-shrink-0">
            <label className="block text-xs font-semibold text-gray-900 uppercase mb-1 ml-1">Net Pay Range</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                placeholder="Min"
                value={minNetPay}
                min="0"
                max="100000000"
                onChange={(e) => {
                  let val = e.target.value;
                  if (val > 100000000) val = 100000000;
                  setMinNetPay(val);
                }}
                className={`w-24 px-2 py-2 border rounded-lg text-sm outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand focus:outline-none ${minNetPay && maxNetPay && Number(maxNetPay) < Number(minNetPay) ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
              <span className="text-gray-400">-</span>
              <input
                type="number"
                placeholder="Max"
                value={maxNetPay}
                min="0"
                max="100000000"
                onChange={(e) => {
                  let val = e.target.value;
                  if (val > 100000000) val = 100000000;
                  setMaxNetPay(val);
                }}
                className={`w-24 px-2 py-2 border rounded-lg text-sm outline-none focus:outline-none focus-visible:outline-none focus:ring-0 focus:border-brand focus:outline-none ${minNetPay && maxNetPay && Number(maxNetPay) < Number(minNetPay) ? 'border-red-500' : 'border-gray-300'
                  }`}
              />
            </div>
            {minNetPay && maxNetPay && Number(maxNetPay) < Number(minNetPay) && (
              <p className="text-[10px] text-red-500 mt-1 absolute">Max must be ≥ Min</p>
            )}
          </div>

          {(searchTerm || statusFilter !== 'all' || runStartDate || runEndDate || minNetPay || maxNetPay) && (
            <div className="flex-shrink-0 ml-auto">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={() => {
                  resetPayrollFilters();
                }}
              >
                Clear All
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Payroll Runs List */}
      {isTableLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
        </div>
      ) : sortedRuns.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 py-16 text-center">
          <p className="text-gray-500 text-sm">No payroll runs found.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-transparent" style={{ backgroundColor: "rgba(59, 130, 246, 0.14)" }}>
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-brand focus:ring-brand"
                      checked={sortedRuns.length > 0 && selectedRows.size === sortedRuns.length}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider select-none">
                    <div className="flex items-center space-x-1">
                      <span>Pay Group</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider select-none">
                    <div className="flex items-center space-x-1">
                      <span>Period</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider select-none">
                    <div className="flex items-center space-x-1">
                      <span>Headcount</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider select-none">
                    <div className="flex items-center space-x-1">
                      <span>Net Pay</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider select-none">
                    <div className="flex items-center space-x-1">
                      <span>Run Date</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>

              <tbody className="bg-white divide-y divide-gray-200">
                {sortedRuns.map((run) => (
                  <tr key={run.id} className={`hover:bg-blue-50/30 ${selectedRows.has(run.id || run._id) ? 'bg-brand/5' : ''}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <input
                        type="checkbox"
                        className="rounded border-gray-300 text-brand focus:ring-brand"
                        checked={selectedRows.has(run.id || run._id)}
                        onChange={() => handleSelectRow(run.id || run._id)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-8 w-8">
                          <div className="h-8 w-8 rounded-full bg-brand flex items-center justify-center">
                            <Calculator className="h-4 w-4 text-white" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{formatPayGroupName(run.payGroup)}</div>
                          {run.runId && (
                            <div className="mt-0.5">
                              <span className="px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded text-[10px] font-bold border border-gray-200 uppercase tracking-tight">
                                {run.runId}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatPeriodDisplay(run)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {run.headcount}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrencyInr(run.netPay)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateValue(run.runDate, { includeTime: true })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => navigate(`/payroll_run/${run.id || run._id}`)}
                          className="text-gray-500 hover:text-brand transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeletePayrollRun(run)}
                          className="text-red-600 hover:text-red-800"
                          disabled={deletePayrollRunMutation.isLoading}
                          title="Delete Run"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {pagination && (
            <PaginationControls
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              limit={pagination.limit}
              hasNextPage={pagination.hasNextPage}
              hasPrevPage={pagination.hasPrevPage}
              onPageChange={setPage}
              onLimitChange={(newLimit) => {
                setLimit(newLimit);
                setPage(1);
              }}
              border={false}
            />
          )}
        </div>
        </div>
      )}

      {/* Run Payroll Modal */}
      {showRunModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Run Payroll</h2>
                <button
                  onClick={handleCloseRunModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pay Group <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={runFormData.payGroup}
                    onChange={(e) => handleRunFormChange('payGroup', e.target.value)}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0 transition-all ${formErrors.payGroup
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-brand focus:border-brand'
                      }`}
                  >
                    <option value="">Select Pay Group</option>
                    <option value="monthly">Monthly Payroll</option>
                  </select>
                  {formErrors.payGroup && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.payGroup}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Period <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="month"
                    key={runFormData.period}  // This forces re-mount when value changes
                    value={runFormData.period || ""}
                    max={new Date().toISOString().slice(0, 7)}
                    onChange={(e) => handleRunFormChange("period", e.target.value)}
                    onClick={(e) => e.target.showPicker()}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-0 transition-all ${formErrors.period
                      ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                      : 'border-gray-300 focus:ring-brand focus:border-brand'
                      }`}
                  />
                  {formErrors.period && (
                    <p className="mt-1 text-sm text-red-600">{formErrors.period}</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end space-x-3 mt-8 pt-6 border-t border-gray-200">
                <Button
                  variant="primary"
                  onClick={handleCloseRunModal}
                  className="bg-brand hover:bg-brand-dark text-white border-transparent"
                >
                  Cancel
                </Button>
                <PermissionGate permission="payroll" action="create">
                  <Button
                    variant="primary"
                    icon={
                      isCreatingRun ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )
                    }
                    onClick={handleRunPayroll}
                    className="bg-brand hover:bg-brand-dark text-white border-transparent"
                    disabled={isCreatingRun}
                  >
                    {isCreatingRun ? 'Processing...' : 'Run Payroll'}
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </div>
        </div>
      )}

      <WarningPopup
        isOpen={showDeleteConfirm}
        onClose={cancelDeletePayrollRun}
        onConfirm={confirmDeletePayrollRun}
        title={isBulkDelete ? "Delete Payroll Runs" : "Delete Payroll Run"}
        message={
          isBulkDelete
            ? `Are you sure you want to delete ${selectedRows.size} selected payroll run(s)? This action cannot be undone.`
            : (runToDelete
              ? `Are you sure you want to delete the ${formatPayGroupName(runToDelete.payGroup || '')} payroll for ${formatPeriodDisplay(runToDelete)}? This action cannot be undone.`
              : "Are you sure you want to delete this payroll run?")
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />

      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        scopedFormatExport={{
          onExport: handleScopedExport,
        }}
        loading={exportLoading}
        title="Export Payroll Runs"
        showDateRange={true}
      />
    </div>
  );
};

export default PayrollRun;